import {
  createBipartite,
  createMembershipGrid,
  type BipartiteHandle,
  type MembershipGridHandle,
} from '@learnforge/demo-render';
import {
  defineExperiment,
  type CustomExperimentSpec,
  type PanelRow,
} from '@learnforge/demo-render/harness';
import type { SetsContext, SetsKey } from '../types';
import type { Pair } from '../sets';
import { isReflexive, isSymmetric, isAntisymmetric, isTransitive, violatingPairs } from '../sets';

// ── State ─────────────────────────────────────────────────────────────────────

export interface State {
  A: string[];
  B: string[];
  pairs: Pair[];
  onOneSet: boolean;
  advanced: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_A = ['a', 'b', 'c'];
const DEFAULT_B = ['x', 'y', 'z'];

/** Relation properties in display order. */
const PROPERTIES: {
  prop: 'reflexive' | 'symmetric' | 'antisymmetric' | 'transitive';
  labelKey: SetsKey;
}[] = [
  { prop: 'reflexive', labelKey: 'prop.reflexive' },
  { prop: 'symmetric', labelKey: 'prop.symmetric' },
  { prop: 'antisymmetric', labelKey: 'prop.antisymmetric' },
  { prop: 'transitive', labelKey: 'prop.transitive' },
];

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** Toggle a pair in a list — add if absent, remove if present. */
export function togglePair(pairs: Pair[], from: string, to: string): Pair[] {
  const exists = pairs.some(([a, b]) => a === from && b === to);
  if (exists) {
    return pairs.filter(([a, b]) => !(a === from && b === to));
  }
  return [...pairs, [from, to] as Pair];
}

/** Compute auto-name for a relation on one set. */
export function autoName(pairs: Pair[], on: string[]): string | null {
  const refl = isReflexive(pairs, on);
  const sym = isSymmetric(pairs);
  const antisym = isAntisymmetric(pairs);
  const trans = isTransitive(pairs);

  if (refl && sym && trans) return 'equivalence';
  if (refl && antisym && trans) return 'partial-order';
  return null;
}

// ── SVG viewBox dimensions ────────────────────────────────────────────────────

const BIPARTITE_WIDTH = 360;
const BIPARTITE_HEIGHT = 340;

// ── Spec ──────────────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, SetsKey, SetsContext> {
  return {
    labelKey: 'experiment.relations.label',
    demoId: 'sets',
    advancedKey: 'relations',

    initialState(ctx): State {
      return {
        A: DEFAULT_A.slice(),
        B: DEFAULT_B.slice(),
        pairs: [
          ['a', 'x'],
          ['b', 'y'],
        ] as Pair[],
        onOneSet: ctx.advanced,
        advanced: ctx.advanced,
      };
    },

    panel(state, ctx): PanelRow<SetsKey, State>[] {
      const advanced = ctx.advanced;
      const rows: PanelRow<SetsKey, State>[] = [
        {
          kind: 'advanced-toggle',
          labelKey: 'btn.advanced',
        },
      ];

      if (advanced) {
        rows.splice(0, 0, {
          kind: 'dropdown',
          id: 'on-one-set',
          labelKey: 'param.on-one-set',
          options: [
            { value: 'false', labelKey: 'param.on-one-set.no' as SetsKey },
            { value: 'true', labelKey: 'param.on-one-set.yes' as SetsKey },
          ],
          value: state.onOneSet ? 'true' : 'false',
          onChange: (v) => ({
            ...state,
            onOneSet: v === 'true',
            // Reset pairs when toggling mode to avoid invalid arrows
            pairs: [],
          }),
        });
      }

      return rows;
    },

    createRender(ctx, host, controls) {
      // ── Container ──
      const container = document.createElement('div');
      container.className = 'relations-container';
      host.appendChild(container);

      // ── Left column: bipartite diagram ──
      const diagramCol = document.createElement('div');
      diagramCol.className = 'relations-diagram-col';
      container.appendChild(diagramCol);

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'relations-svg');
      svg.setAttribute('viewBox', `0 0 ${BIPARTITE_WIDTH} ${BIPARTITE_HEIGHT}`);
      svg.setAttribute('width', String(BIPARTITE_WIDTH));
      svg.setAttribute('height', String(BIPARTITE_HEIGHT));
      diagramCol.appendChild(svg);

      // Column headers rendered inside SVG
      const headerLeft = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      headerLeft.setAttribute('class', 'relations-col-header');
      headerLeft.setAttribute('x', '80');
      headerLeft.setAttribute('y', '28');
      headerLeft.setAttribute('text-anchor', 'middle');
      svg.appendChild(headerLeft);

      const headerRight = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      headerRight.setAttribute('class', 'relations-col-header');
      headerRight.setAttribute('x', '280');
      headerRight.setAttribute('y', '28');
      headerRight.setAttribute('text-anchor', 'middle');
      svg.appendChild(headerRight);

      // ── Right column: matrix + readout + badges ──
      const infoCol = document.createElement('div');
      infoCol.className = 'relations-info-col';
      container.appendChild(infoCol);

      // Cardinality readout
      const readout = document.createElement('div');
      readout.className = 'relations-readout';
      infoCol.appendChild(readout);

      // Matrix wrapper
      const matrixWrapper = document.createElement('div');
      matrixWrapper.className = 'relations-matrix';
      infoCol.appendChild(matrixWrapper);

      // Property badges (advanced only)
      const badgesEl = document.createElement('div');
      badgesEl.className = 'relations-badges';
      infoCol.appendChild(badgesEl);

      // Auto-name display
      const nameEl = document.createElement('div');
      nameEl.className = 'relations-name';
      infoCol.appendChild(nameEl);

      // ── Handles ──
      let bipartiteHandle: BipartiteHandle | null = null;
      let gridHandle: MembershipGridHandle | null = null;

      function update(state: State): void {
        const { A, pairs } = state;
        // When onOneSet, B = A for the relation-on-A view
        const B = state.onOneSet ? A : state.B;

        // Compute violating pairs for all props when onOneSet
        const violatingSet: Set<string> = new Set();
        if (state.onOneSet) {
          for (const { prop } of PROPERTIES) {
            const vp = violatingPairs(prop, pairs, A);
            for (const [a, b] of vp) {
              violatingSet.add(`${a}:${b}`);
            }
          }
        }

        // ── Bipartite diagram ──
        const leftNodes = A.map((id) => ({ id, label: id }));
        const rightNodes = B.map((id) => ({ id: `right:${id}`, label: id }));

        const arrows = pairs.map(([from, to]) => {
          const key = `${from}:${to}`;
          const isViolating = state.onOneSet && violatingSet.has(key);
          if (isViolating) {
            return { from, to: `right:${to}`, className: 'is-violating' };
          }
          return { from, to: `right:${to}` };
        });

        headerLeft.textContent = 'A';
        headerRight.textContent = state.onOneSet ? 'A' : 'B';

        if (bipartiteHandle === null) {
          bipartiteHandle = createBipartite(svg, {
            left: leftNodes,
            right: rightNodes,
            arrows,
            onToggleArrow(from, toFull) {
              // toFull is "right:<id>" — strip prefix
              const to = toFull.replace(/^right:/, '');
              controls.setState((prev) => ({
                ...prev,
                pairs: togglePair(prev.pairs, from, to),
              }));
            },
          });
        } else {
          bipartiteHandle.update({ left: leftNodes, right: rightNodes, arrows });
        }

        // ── Relation matrix ──
        const matrixCols = B.map((id) => ({ id, label: id }));
        const pairSet = new Set(pairs.map(([a, b]) => `${a}:${b}`));

        const gridOpts = {
          universe: A,
          columns: matrixCols,
          isMember(i: number, colId: string): boolean {
            const elem = A[i] ?? '';
            return pairSet.has(`${elem}:${colId}`);
          },
          highlightColumn: null as string | null,
        };

        if (gridHandle === null) {
          gridHandle = createMembershipGrid(matrixWrapper, gridOpts);
        } else {
          gridHandle.update(gridOpts);
        }

        // ── Cardinality readout ──
        readout.textContent = `${ctx.i18n.t('stat.relation-size')}: |R| = ${pairs.length}`;

        // ── Property badges (advanced / onOneSet only) ──
        badgesEl.innerHTML = '';
        nameEl.textContent = '';

        if (state.onOneSet) {
          for (const { prop, labelKey } of PROPERTIES) {
            let holds: boolean;
            switch (prop) {
              case 'reflexive':
                holds = isReflexive(pairs, A);
                break;
              case 'symmetric':
                holds = isSymmetric(pairs);
                break;
              case 'antisymmetric':
                holds = isAntisymmetric(pairs);
                break;
              case 'transitive':
                holds = isTransitive(pairs);
                break;
            }

            const badge = document.createElement('span');
            badge.className = holds
              ? 'relations-badge relations-badge--true'
              : 'relations-badge relations-badge--false';
            badge.textContent = ctx.i18n.t(labelKey);
            badgesEl.appendChild(badge);
          }

          const name = autoName(pairs, A);
          if (name !== null) {
            const nameKey = name === 'equivalence' ? 'name.equivalence' : 'name.partial-order';
            nameEl.textContent = `→ ${ctx.i18n.t(nameKey as SetsKey)}`;
            nameEl.className = 'relations-name relations-name--active';
          } else {
            nameEl.className = 'relations-name';
          }
        }
      }

      return {
        update,
        dispose() {
          bipartiteHandle?.destroy();
          bipartiteHandle = null;
          gridHandle?.destroy();
          gridHandle = null;
        },
      };
    },

    actions(state, ctx) {
      // The diagonal (identity) preset only makes sense when B = A (onOneSet).
      // Gate it on ctx.advanced, which is the only mode that exposes the
      // on-one-set dropdown. Never show it in foundation mode where B ≠ A and
      // the identity pairs would reference absent right-nodes.
      const basePresets: { id: string; labelKey: SetsKey }[] = [
        { id: 'empty', labelKey: 'preset.empty' as SetsKey },
        { id: 'all', labelKey: 'preset.all' as SetsKey },
      ];
      const presets = ctx.advanced
        ? [...basePresets, { id: 'diagonal', labelKey: 'preset.diagonal' as SetsKey }]
        : basePresets;

      return {
        onReset() {
          return {
            A: DEFAULT_A.slice(),
            B: DEFAULT_B.slice(),
            pairs: [
              ['a', 'x'],
              ['b', 'y'],
            ] as Pair[],
            onOneSet: ctx.advanced,
            advanced: ctx.advanced,
          };
        },
        onRandomise() {
          const A = state.A;
          const B = state.onOneSet ? A : state.B;
          const newPairs: Pair[] = [];
          for (const a of A) {
            for (const b of B) {
              // Include each pair with ~40% probability
              if (ctx.prng() < 0.4) {
                newPairs.push([a, b] as Pair);
              }
            }
          }
          return { ...state, pairs: newPairs };
        },
        presets,
        onPreset(s: State, id: string) {
          const A = s.A;
          const B = s.onOneSet ? A : s.B;
          switch (id) {
            case 'empty':
              return { ...s, pairs: [] };
            case 'all':
              return {
                ...s,
                pairs: A.flatMap((a) => B.map((b) => [a, b] as Pair)),
              };
            case 'diagonal':
              // Identity relation: (a,a) for a in A.
              // Always force onOneSet:true and B:=A so the diagram renders
              // all identity arrows — never produce pairs targeting absent right-nodes.
              return {
                ...s,
                onOneSet: true,
                B: A.slice(),
                pairs: A.map((a) => [a, a] as Pair),
              };
            default:
              return s;
          }
        },
      };
    },

    applyShotState(state): State {
      // Advanced shot: relation on A that is symmetric + transitive (equivalence on {a,b,c})
      const A = DEFAULT_A.slice();
      // Equivalence: all pairs within blocks {a,b} and {c}
      const pairs: Pair[] = [
        ['a', 'a'],
        ['a', 'b'],
        ['b', 'a'],
        ['b', 'b'],
        ['c', 'c'],
      ];
      return {
        ...state,
        A,
        B: A,
        pairs,
        onOneSet: true,
        advanced: true,
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
